import React, { useState } from "react";
import DetachedModal from "../../../../Shared/Modals/DetachedModal/DetachedModal";
import { Input } from "../../../../ui/input";
import { Button } from "../../../../Shared/Buttons/Button/Button";
import { EDSButton } from "../../../../eds/EDSButton";
import { Deposit } from "../../../../../types/bookings";
import {
  parseDepositAmount,
  formatCurrency,
  getCurrencySymbol
} from "../../../../../utils/number";

type ClaimModalProps = {
  deposit: Deposit;
  onClose: () => void;
  onSubmit: (claimData: ClaimFormData) => Promise<void>;
};

export type ClaimFormData = {
  amount: number;
};

const ClaimModal = ({ deposit, onClose, onSubmit }: ClaimModalProps) => {
  const [formData, setFormData] = useState<ClaimFormData>({
    amount: 0
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const currency = deposit.currency || "GBP";
  const depositAmount = parseDepositAmount(deposit.amount);

  const totalClaimed =
    deposit.claims?.reduce(
      (sum, claim) => sum + parseDepositAmount(claim.amount),
      0
    ) || 0;

  const maxClaimAmount = depositAmount - totalClaimed;

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.amount || formData.amount <= 0) {
      newErrors.amount = "Claim amount must be greater than 0";
    }

    if (formData.amount > maxClaimAmount) {
      newErrors.amount = `Claim amount cannot exceed ${formatCurrency(maxClaimAmount, currency)}`;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmit(formData);
      onClose();
    } catch (error) {
      console.error("Error processing claim:", error);
      setErrors({ submit: "Failed to process claim. Please try again." });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field: keyof ClaimFormData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handleMaxClaim = () => {
    handleInputChange("amount", maxClaimAmount);
  };

  return (
    <DetachedModal heading="Claim Deposit" width="500px" handleClose={onClose}>
      <div className="ClaimModal flex flex-col h-full gap-6">
        <div className="ClaimModal-Info rounded-md p-4 border border-[var(--borderColor)]">
          <div className="ClaimModal-InfoRow flex justify-between items-center mb-2 gap-4">
            <span className="ClaimModal-Label font-medium text-[color:var(--textColorSecondary)] text-sm">
              Original Deposit:
            </span>
            <span className="ClaimModal-Value font-semibold text-[color:var(--textColor)] text-sm break-all">
              {formatCurrency(depositAmount, currency)}
            </span>
          </div>
          <div className="ClaimModal-InfoRow flex justify-between items-center mb-2 gap-4">
            <span className="ClaimModal-Label font-medium text-[color:var(--textColorSecondary)] text-sm">
              Already Claimed:
            </span>
            <span className="ClaimModal-Value font-semibold text-[color:var(--textColor)] text-sm break-all">
              {formatCurrency(totalClaimed, currency)}
            </span>
          </div>
          <div className="ClaimModal-InfoRow flex justify-between items-center mb-2 gap-4">
            <span className="ClaimModal-Label font-medium text-[color:var(--textColorSecondary)] text-sm">
              Available to Claim:
            </span>
            <span className="ClaimModal-Value font-semibold text-[color:var(--textColor)] text-sm break-all">
              {formatCurrency(maxClaimAmount, currency)}
            </span>
          </div>
          <div className="ClaimModal-InfoRow flex justify-between items-center mb-2 gap-4">
            <span className="ClaimModal-Label font-medium text-[color:var(--textColorSecondary)] text-sm">
              Deposit Date:
            </span>
            <span className="ClaimModal-Value font-semibold text-[color:var(--textColor)] text-sm break-all">
              {new Date(deposit.date).toLocaleDateString(undefined, {
                dateStyle: "medium"
              })}
            </span>
          </div>
          {deposit.payment_ref && (
            <div className="ClaimModal-InfoRow ClaimModal-InfoRow--PaymentRef flex justify-between items-center mb-0 gap-0">
              <span className="ClaimModal-Label font-medium text-[color:var(--textColorSecondary)] text-sm basis-3/5 shrink-0">
                Payment Reference:
              </span>
              <span className="ClaimModal-Value font-semibold text-[color:var(--textColor)] text-sm break-all basis-2/5 shrink-0">
                {deposit.payment_ref}
              </span>
            </div>
          )}
        </div>

        <div className="ClaimModal-Form flex flex-col gap-4">
          <div className="ClaimModal-Field flex flex-col gap-2">
            <label
              htmlFor="claim-amount"
              className="font-medium text-[color:var(--textColor)] text-sm"
            >
              Claim Amount *
            </label>
            <div className="ClaimModal-AmountContainer flex items-center gap-2 relative">
              <span className="ClaimModal-CurrencyPrefix font-medium text-[color:var(--textColor)] text-base shrink-0">
                {getCurrencySymbol(currency)}
              </span>
              <Input
                id="claim-amount"
                type="number"
                value={formData.amount.toString()}
                onChange={(e) =>
                  handleInputChange("amount", parseFloat(e.target.value) || 0)
                }
                placeholder="0.00"
                step="0.01"
                min="0"
                max={maxClaimAmount}
                maxLength={15}
              />
              <EDSButton
                onClick={handleMaxClaim}
                disabled={isSubmitting}
                variant="outline"
                size="sm"
              >
                Max
              </EDSButton>
            </div>
            {errors.amount && (
              <span className="ClaimModal-Error text-[color:var(--errorColor,#d32f2f)] text-xs mt-1">
                {errors.amount}
              </span>
            )}
          </div>

          {errors.submit && (
            <div className="ClaimModal-Error ClaimModal-SubmitError bg-[var(--errorBackgroundColor,#ffebee)] border border-[var(--errorColor,#d32f2f)] rounded p-3 mt-2 text-[color:var(--errorColor,#d32f2f)] text-xs">
              {errors.submit}
            </div>
          )}
        </div>

        <div className="ClaimModal-Actions mt-auto pt-4 shrink-0 flex gap-3 justify-end border-t border-[var(--borderColor)]">
          <Button
            onClick={onClose}
            disabled={isSubmitting}
            fill="outline"
            size="md"
            type="default"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting}
            fill="solid"
            size="md"
            type="primary"
          >
            {isSubmitting ? "Processing..." : "Claim Deposit"}
          </Button>
        </div>
      </div>
    </DetachedModal>
  );
};

export default ClaimModal;
